/**
 * AiUseCaseWizard — the eight-step intake flow for the AI Use Case Register.
 *
 * Ported 1:1 from the FAIR app's AI Governance module, then extended with the EU AI Act steps.
 * The shell owns everything that isn't a field: the page header, the step rail, the completeness
 * gating and the Back/Next bar. Each step file renders only its own fields.
 *
 * Deliberate differences from the source:
 *  1. Step 3's fields come from ReactQuill, which emits '<p><br></p>' for an empty
 *     editor — emptiness is tested on the stripped text, not the raw HTML.
 *  2. The source locked steps positionally (`stepNum > step`), so deep-linking to a
 *     later step silently unlocked everything before it. Locks are derived from
 *     canAdvance() on each prior step instead.
 *
 * ── Why the AI Act steps were originally APPENDED (7, 8) rather than inserted ──
 *
 * Step numbers are positional in the URL: `App.tsx:578` routes
 * `/ai-use-cases/:useCaseId/intake/:step`, `:step` is parsed as an integer, and the step body is
 * a hardcoded `activeStep === N` ladder. There is no slug indirection and no redirect table, so
 * renumbering a step changes what `/intake/N` means for every bookmark and every in-flight draft.
 * That is why, when the AI Act steps were first added, they went on the end rather than being
 * inserted mid-flow.
 *
 * ── Steps 6 and 7 were later swapped by product decision ──
 *
 * AI Act Classification (originally step 7) and Linked Artefacts (originally step 6) have traded
 * places: Classification is now 6, Linked Artefacts is now 7. `Step6Classification.tsx` and
 * `Step7Links.tsx` are named for where they sit today, not for where they started — see each
 * file's own header for what it used to be. The renumbering cost described above was paid
 * deliberately here, on the reasoning that Classification reads more naturally right after
 * Data & Privacy than after an optional linking step. `canAdvance`'s per-step `switch` and the
 * render ladder below were updated in lockstep, and the five hardcoded "Step 7: ..." validation
 * messages in `prod-nimbus-ai-use-cases/lambda_function.py` were changed to "Step 6: ...".
 *
 * The one real side effect: Classification (6) and Oversight & Vendors (8) — the two steps that
 * are specifically about the AI Act — are no longer adjacent, since Linked Artefacts now sits
 * between them at 7.
 *
 * ── Why `optional` is a flag on STEPS and not `STEPS.length - 1` ──
 *
 * The old `GATED_STEPS = STEPS.length - 1` encoded "the optional step is the last one", which
 * stopped being true the moment anything was appended or reordered — the progress denominator
 * would have counted the wrong step as optional. Making optionality a property of the step
 * instead of an assumption about its index fixed it for good, and is exactly what made the 6/7
 * swap above safe: the `optional: true` flag travels with Linked Artefacts wherever it sits, so
 * moving it did not have to touch the progress-denominator or submission-gate logic at all.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { isRichTextEmpty } from '../../../components/RichTextField';
import { useToast } from '../../../hooks/useToast';
import { useSwal } from '../../../hooks/useSwal';
import { aiUseCaseApi, linkedArtefactsApi } from '../../../services/api';
import { AI_STATUS_LABELS, AI_STATUS_TONE, AI_TYPE_LABELS } from '../../../constants/aiGovernance';
import { aiUseCaseKeys, invalidateAiUseCaseLists, writeAiUseCase } from '../aiUseCaseKeys';
import {
  AI_ACT_CLASSIFICATION_LABELS,
  AI_ACT_CLASSIFICATION_TONE,
  emptyHumanOversight,
  emptyMonitoring,
  hasAnyOversight,
  isBlocking,
  needsAutomatedDecisionDetails,
  requiresAiRiskAssessment,
} from '../../../constants/aiAct';
import type { AiUseCase, AiUseCaseStatus } from '../../../types/models';
import Step1AiType from './Step1AiType';
import Step2Identity from './Step2Identity';
import Step3BusinessCase from './Step3BusinessCase';
import Step4Outcomes from './Step4Outcomes';
import Step5Data from './Step5Data';
import Step6Classification from './Step6Classification';
import Step7Links from './Step7Links';
import Step8Oversight from './Step8Oversight';
import Step9Review from './Step9Review';

export type { StepProps } from './types';

interface WizardStep {
  label: string;
  icon: string;
  /**
   * A step with no completeness requirement: `canAdvance` always passes it, it never blocks the
   * steps after it, and it is excluded from the progress denominator and from the submission
   * gate. Optionality lives here rather than being inferred from the step's position — see the
   * file header.
   */
  optional?: boolean;
}

/** Cheap deep-equal over JSON-plain values — every AiUseCase field is a string, boolean,
 *  array or plain object, never a function/Date/undefined, so this is exact. */
const sameValue = (a: unknown, b: unknown): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

const STEPS: WizardStep[] = [
  { label: 'AI Type',              icon: 'ri-robot-line' },
  { label: 'Identity',             icon: 'ri-user-settings-line' },
  { label: 'Business Case',        icon: 'ri-briefcase-line' },
  { label: 'Outcomes',             icon: 'ri-target-line' },
  { label: 'Data & Privacy',       icon: 'ri-shield-user-line' },
  { label: 'AI Act Classification', icon: 'ri-scales-3-line' },
  { label: 'Linked Artefacts',     icon: 'ri-links-line', optional: true },
  { label: 'Oversight & Vendors',  icon: 'ri-eye-line' },
  { label: 'Review & Confirm',     icon: 'ri-checkbox-multiple-line' },
];

/** How many steps actually gate submission — the progress denominator. */
const GATED_STEP_COUNT = STEPS.filter(s => !s.optional).length;

/**
 * Statuses the API refuses to write at all (`writable_for_status` returns nothing, and a PUT
 * answers 409). The wizard is an editor, so rather than render one that cannot save — or worse,
 * accept keystrokes and drop them — it hands these records to the read-only detail page.
 * `active` is deliberately NOT here: the API keeps ownership, oversight, vendors, transfers,
 * monitoring and the review cadence editable on a live record.
 */
const READ_ONLY_STATUSES = new Set<AiUseCaseStatus>(['rejected', 'retired']);

/** ReactQuill leaves markup behind in a visually empty editor, so a rich-text field
 *  only counts as filled once its stripped text is non-empty. Delegates to the shared
 *  helper that ships with RichTextField. */
const hasRichText = (html: string): boolean => !isRichTextEmpty(html || '');

/**
 * Per-step completeness. Steps 1-5 are ported from the source wizard (the only change being the
 * stripHtml treatment of step 3); 7 and 8 mirror `validate_ai_act` in
 * `prod-nimbus-ai-use-cases/lambda_function.py:849-882` clause for clause, because a client gate
 * the server does not share blocks a record the API would accept, and a server gate the client
 * does not share turns Submit into an unexplained 400.
 *
 * The fallthrough returns FALSE. It previously returned true, which meant any step added without
 * a clause here was silently always-passable *and* always-reachable — the failure mode was a new
 * required step that could be walked straight past. An unrecognised step number is now treated
 * as incomplete, so the mistake shows up as a stuck Continue button instead of a hole in the gate.
 */
export function canAdvance(uc: AiUseCase, step: number): boolean {
  switch (step) {
    case 1:
      return !!uc.aiType;
    case 2:
      return !!((uc.name || '').trim() && (uc.summary || '').trim() && (uc.departmentId || '').trim() && (uc.owner || '').trim() && (uc.raciResponsible || '').trim() && (uc.raciInfoSec || '').trim());
    case 3:
      return hasRichText(uc.businessProblem) && hasRichText(uc.businessValue) && hasRichText(uc.alternatives);
    case 4:
      return (uc.outcomes || []).length > 0 && (uc.outcomes || []).every(o => !!((o.outcome || '').trim() && (o.metric || '').trim()));
    case 5: {
      const fieldsOk = (uc.dataFields || []).length > 0 && (uc.dataFields || []).every(f => (f.name || '').trim() && (f.processingPurpose || '').trim());
      // 'customer_controlled' counts, matching the server's has_personal check — a non-empty
      // string is truthy in JS regardless of which of the three values it holds, so this has
      // to compare against 'no' explicitly rather than test f.isPersonalData for truthiness.
      const hasPersonal = (uc.dataFields || []).some(f => f.isPersonalData !== 'no');
      if (hasPersonal && (!(uc.legalBasis || '') || !(uc.processingRationale || '').trim() || !(uc.dataRetention || '').trim())) return false;
      if (uc.thirdPartySharing && !(uc.thirdPartyDetails || '').trim()) return false;
      return fieldsOk;
    }
    case 6: {
      const classification = uc.aiActClassification || '';
      // Article 5 is a stop condition, not an incomplete field: nothing downstream unlocks.
      if (isBlocking(classification)) return false;
      if (!classification) return false;
      if (!uc.actRole) return false;
      // Gated on the decisions flag ONLY, never on profiling — see the helper for why. Routed
      // through it rather than inlined, because the inlined copy is what let the detail page
      // and the PDF drift onto the broader rule and render an unsatisfiable Required field.
      if (needsAutomatedDecisionDetails({ makesAutomatedDecisions: !!uc.makesAutomatedDecisions })
          && !(uc.automatedDecisionDetails || '').trim()) return false;
      if (classification === 'high_risk' && !hasRichText(uc.classificationRationale)) return false;
      if (requiresAiRiskAssessment(classification) && (uc.aiRiskAssessmentIds || []).length === 0) return false;
      return true;
    }
    case 7:
      // Linked artefacts. Optional by design — a use case can be submitted with no links.
      return true;
    case 8: {
      const classification = uc.aiActClassification || '';
      if (isBlocking(classification)) return false;
      // Effort scales with risk (NFR-02). Only the high-risk tier turns the oversight block into
      // a requirement; every other tier passes this step with it empty.
      if (classification !== 'high_risk') return true;
      const oversight = uc.humanOversight ?? emptyHumanOversight();
      if (!(oversight.accountablePersonId || '').trim()) return false;
      return hasAnyOversight(oversight);
    }
    case 9:
      // Review & Confirm has no fields of its own to complete - reaching it at all already
      // means every gated step before it passed (stepStates below only marks a step reachable
      // once every non-optional step ahead of it is complete), so there is nothing left to gate.
      return true;
    default:
      return false;
  }
}

/** The API writes every attribute on create, but a record saved before a field existed
 *  would crash a step that assumes an array/string. Normalise once, on load.
 *
 *  Every field added to `AiUseCase` needs an entry: each of the 21 AI Act fields below is
 *  absent from every record written before this release, and `uc.vendors.map(...)` on an
 *  undefined list throws rather than rendering an empty step. The two object fields are
 *  rebuilt from their factories with the stored value spread over the top, so a record written
 *  against an older shape of `HumanOversight` gains the missing keys instead of leaving
 *  `oversight.hasKillSwitch` undefined. */
const withDefaults = (uc: AiUseCase): AiUseCase => ({
  ...uc,
  aiType: uc.aiType || '',
  status: uc.status || 'draft',
  name: uc.name || '',
  summary: uc.summary || '',
  departmentId: uc.departmentId || '',
  owner: uc.owner || '',
  raciResponsible: uc.raciResponsible || '',
  raciInfoSec: uc.raciInfoSec || '',
  raciLegal: uc.raciLegal || '',
  raciInformed: uc.raciInformed || '',
  businessProblem: uc.businessProblem || '',
  businessValue: uc.businessValue || '',
  alternatives: uc.alternatives || '',
  outcomes: uc.outcomes || [],
  dataFields: uc.dataFields || [],
  legalBasis: uc.legalBasis || '',
  processingRationale: uc.processingRationale || '',
  dataRetention: uc.dataRetention || '',
  thirdPartySharing: uc.thirdPartySharing ?? false,
  thirdPartyDetails: uc.thirdPartyDetails || '',
  linkedRiskIds: uc.linkedRiskIds || [],
  aiRiskAssessmentIds: uc.aiRiskAssessmentIds || [],

  // ── AI Act: classification, GPAI axis and role (step 7) ──
  aiActClassification: uc.aiActClassification || '',
  classificationRationale: uc.classificationRationale || '',
  isGpai: uc.isGpai ?? false,
  isGpaiSystemic: uc.isGpaiSystemic ?? false,
  actRole: uc.actRole || '',
  makesAutomatedDecisions: uc.makesAutomatedDecisions ?? false,
  involvesProfiling: uc.involvesProfiling ?? false,
  automatedDecisionDetails: uc.automatedDecisionDetails || '',

  // ── Oversight, supply chain, monitoring and review (step 8) ──
  humanOversight: { ...emptyHumanOversight(), ...(uc.humanOversight ?? {}) },
  vendors: uc.vendors || [],
  transfers: uc.transfers || [],
  monitoring: { ...emptyMonitoring(), ...(uc.monitoring ?? {}) },
  reviewFrequency: uc.reviewFrequency || '',
  nextReviewDate: uc.nextReviewDate || '',

  // ── System-owned: never edited here, but read by the header and the rail ──
  dpiaVerdict: uc.dpiaVerdict || '',
  linkedDpiaId: uc.linkedDpiaId || '',
  lastScreenedAt: uc.lastScreenedAt || '',
  decision: uc.decision || '',
  decidedBy: uc.decidedBy || '',
  decidedAt: uc.decidedAt || '',
  decisionConditions: uc.decisionConditions || '',
});

/** The API client throws `API Error: <status> - <json body>`. */
interface ApiErrorBody {
  error?: string;
  validationErrors?: string[];
  frozenAttributes?: string[];
}
const parseApiErrorBody = (err: unknown): ApiErrorBody | null => {
  const message = err instanceof Error ? err.message : '';
  const jsonStart = message.indexOf('{');
  if (jsonStart < 0) return null;
  try {
    return JSON.parse(message.slice(jsonStart)) as ApiErrorBody;
  } catch {
    return null;
  }
};

/** The transition endpoint returns per-step `validationErrors` worth showing rather than
 *  swallowing, and the update endpoint's 409 names the attributes it refused. Fine for a
 *  short, single-line toast; a multi-item validationErrors list belongs in swal.errorList
 *  instead (see submitMutation below) - a toast clips/auto-dismisses before a long list can
 *  be read. */
const describeApiError = (err: unknown, fallback: string): string => {
  const body = parseApiErrorBody(err);
  if (body?.error) {
    const detail = body.validationErrors?.length ? ` ${body.validationErrors.join(' ')}` : '';
    const frozen = body.frozenAttributes?.length
      ? ` Locked: ${body.frozenAttributes.join(', ')}.`
      : '';
    return `${body.error}.${detail}${frozen}`;
  }
  return fallback;
};

export const AiUseCaseWizard: React.FC = () => {
  const { useCaseId, step: stepParam } = useParams<{ useCaseId: string; step: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const parsedStep = Number.parseInt(stepParam ?? '1', 10);
  const isNew = !useCaseId;

  const [draft, setDraft] = useState<AiUseCase | null>(null);
  const [savingStatus, setSavingStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  /** Which attributes the user has touched since the last successful save — the wizard
   *  PUTs a real patch so the audit log's changedKeys stay meaningful. */
  const dirtyKeys = useRef(new Set<keyof AiUseCase>());
  const seededId = useRef<string | null>(null);
  const createStarted = useRef(false);
  const bouncedReadOnly = useRef(false);

  /** Timer that clears the transient "Saved" flash. Tracked so a second save starting
   *  within the two seconds cannot have its "Saving…" wiped by the previous timer. */
  const savedFlashTimer = useRef<number | null>(null);
  const flashSaved = useCallback(() => {
    if (savedFlashTimer.current !== null) window.clearTimeout(savedFlashTimer.current);
    setSavingStatus('saved');
    savedFlashTimer.current = window.setTimeout(() => {
      savedFlashTimer.current = null;
      setSavingStatus('idle');
    }, 2000);
  }, []);
  const beginSaving = useCallback(() => {
    if (savedFlashTimer.current !== null) {
      window.clearTimeout(savedFlashTimer.current);
      savedFlashTimer.current = null;
    }
    setSavingStatus('saving');
  }, []);
  useEffect(() => () => {
    if (savedFlashTimer.current !== null) window.clearTimeout(savedFlashTimer.current);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: aiUseCaseKeys.detail(useCaseId),
    queryFn: () => aiUseCaseApi.get(useCaseId!),
    enabled: !!useCaseId,
  });

  /**
   * Links live in the artefacts API, not on the use case, and are written immediately and
   * bidirectionally by step 6 — so the only honest source for "has anything been linked" is
   * that API. The rail used to read `draft.linkedRiskIds.length`, which nothing has written
   * since the risk-only picker was replaced, so step 6 read "Optional" forever no matter how
   * many links existed. Same query key as `LinkedArtefacts`, so while step 6 is mounted this
   * shares its cache entry instead of issuing a second request.
   */
  const { data: linkedCount = 0 } = useQuery({
    queryKey: ['linkedArtefacts', useCaseId, 'ai_use_case'],
    queryFn: () => linkedArtefactsApi.getLinks(useCaseId!, 'ai_use_case'),
    enabled: !!useCaseId,
    select: (res: { links?: unknown[] }) => (res?.links ?? []).length,
  });

  // Seed the editable copy once per record. Re-seeding on every query change would
  // discard in-flight edits every time a save writes the server's copy back to cache;
  // reconciliation after a save is handled by reconcileDraft below, which is narrower.
  useEffect(() => {
    if (!data || seededId.current === data.useCaseId) return;
    seededId.current = data.useCaseId;
    dirtyKeys.current.clear();
    setDraft(withDefaults(data));
  }, [data]);

  /**
   * Dirty a key only when the value actually changes from what's currently on the draft.
   *
   * Until 2026-08-04 this dirtied a key on every call, unconditionally. ReactQuill's mount
   * call to `setContents` carries no `source` argument, which defaults to Quill's own
   * `'api'` — and `RichTextField` forwards `onChange` without checking it — so opening step 7
   * on a record with an empty `classificationRationale` fired `update({ classificationRationale:
   * '<p><br></p>' })` before the user touched anything. That's a no-op change ('' is empty,
   * '<p><br></p>' is Quill's empty), but the old `update` couldn't tell a no-op from a real
   * edit, so it dirtied the field anyway. The next save then sent it, the server compared it
   * against a frozen field's stored value, saw a genuine difference in the raw string, and
   * refused the whole save with a 409 — silently reopening the exact wizard dead end the
   * non-draft reachability fix above exists to prevent.
   *
   * Comparing against `prev` (not the `draft` closure) is what makes this safe in a
   * `useCallback` with no dependencies: `prev` is always the true current value at the moment
   * this applies, so it can't compare against a stale snapshot the way a captured `draft`
   * would. `dirtyKeys` is a ref mutated inside the updater; that's fine here because
   * `Set.add` is idempotent, so a StrictMode double-invoke can't add a key twice.
   */
  const update = useCallback((patch: Partial<AiUseCase>) => {
    setDraft(prev => {
      if (!prev) return prev;
      (Object.keys(patch) as (keyof AiUseCase)[]).forEach(key => {
        if (!sameValue(patch[key], prev[key])) dirtyKeys.current.add(key);
      });
      return { ...prev, ...patch };
    });
  }, []);

  /**
   * Fold the server's normalised record back into the draft after a save.
   *
   * Normalisation is cross-field and server-side: `automatedDecisionDetails` is cleared unless
   * `makesAutomatedDecisions`, `isGpaiSystemic` implies `isGpai`, blank vendor and transfer rows
   * are dropped, `thirdPartyDetails` is cleared with `thirdPartySharing`, an unparseable date
   * becomes ''. Without this the wizard kept showing the value the server had just discarded,
   * and re-sent it the next time the key was dirtied.
   *
   * Keys still in `dirtyKeys` keep the LOCAL value: the user may have typed into a field while
   * the request was in flight, and the response predates those keystrokes. Those keys are still
   * dirty, so the next save sends them — which is why the keys just written are removed from the
   * set before this runs, rather than the whole set being cleared.
   */
  const reconcileDraft = useCallback((saved: AiUseCase) => {
    setDraft(prev => {
      const normalised = withDefaults(saved);
      if (!prev) return normalised;
      const keptLocal: Partial<AiUseCase> = {};
      dirtyKeys.current.forEach(key => {
        (keptLocal as Record<string, unknown>)[key] = prev[key];
      });
      return { ...normalised, ...keptLocal };
    });
  }, []);

  /* Cache plumbing lives in `aiUseCaseKeys`, not here. The wizard was the last surface
   * composing its keys inline, and it read `['ai-use-case', id]` while invalidating
   * `['aiUseCase', id]` — two different keys one character apart. Nothing ever invalidated the
   * key the wizard actually read, so with the 5-minute global staleTime (App.tsx:119) a record
   * edited elsewhere, or a step whose gate had since been satisfied, was served from a stale
   * cache entry on reopen and bounced the user back down the rail.
   *
   * `writeAiUseCase` then `invalidateAiUseCaseLists` is the pairing: seed the record we just
   * got back from the server, mark the lists we did not. */

  // ── /ai-use-cases/new — mint a draft, then continue as that draft's wizard ──
  const createMutation = useMutation({
    mutationFn: () => aiUseCaseApi.create({}),
    onSuccess: created => {
      writeAiUseCase(queryClient, created);
      invalidateAiUseCaseLists(queryClient);
      navigate(`/ai-use-cases/${created.useCaseId}/intake/1`, { replace: true });
    },
    onError: err => {
      toast.error(describeApiError(err, 'Could not start a new AI use case.'));
      navigate('/ai-use-cases', { replace: true });
    },
  });

  useEffect(() => {
    if (!isNew || createStarted.current) return;
    createStarted.current = true; // StrictMode double-invokes effects — never mint two drafts
    createMutation.mutate();
    // createMutation is recreated each render; the ref above is the real guard.
  }, [isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMutation = useMutation({
    mutationFn: (patch: Partial<AiUseCase>) => aiUseCaseApi.update(useCaseId!, patch),
    onMutate: () => beginSaving(),
    onSuccess: (saved, patch) => {
      flashSaved();
      // `patch` is exactly what went to the server, so its keys are the ones now clean.
      // Anything dirtied while the request was in flight is not in it and stays dirty.
      (Object.keys(patch) as (keyof AiUseCase)[]).forEach(key => dirtyKeys.current.delete(key));
      reconcileDraft(saved);
      writeAiUseCase(queryClient, saved);
      invalidateAiUseCaseLists(queryClient);
    },
    onError: () => setSavingStatus('error'),
  });

  const submitMutation = useMutation({
    mutationFn: () => aiUseCaseApi.transition(useCaseId!, 'submitted'),
    onSuccess: updated => {
      writeAiUseCase(queryClient, updated);
      invalidateAiUseCaseLists(queryClient);
      toast.success(`${updated.useCaseId} submitted for review.`);
      navigate(`/ai-use-cases/${updated.useCaseId}`);
    },
    onError: err => {
      const body = parseApiErrorBody(err);
      if (body?.validationErrors?.length) {
        swal.errorList(body.error || 'This AI use case is not complete enough to submit for review', body.validationErrors);
      } else {
        toast.error(describeApiError(err, 'Could not submit this AI use case.'));
      }
    },
  });

  /** Persist the touched attributes. Returns false if the save failed, so callers can
   *  abort the navigation rather than moving on from unsaved work. */
  const persist = async (uc: AiUseCase): Promise<boolean> => {
    if (dirtyKeys.current.size === 0) return true;
    const patch: Partial<AiUseCase> = {};
    dirtyKeys.current.forEach(key => { (patch as Record<string, unknown>)[key] = uc[key]; });
    try {
      // The dirty set is cleaned key-by-key in onSuccess, from the patch itself — clearing it
      // here would also discard keys the user touched while this request was in flight, and
      // those edits would then never be sent.
      await saveMutation.mutateAsync(patch);
      return true;
    } catch (err) {
      toast.error(describeApiError(err, 'Could not save your progress. Please try again.'));
      return false;
    }
  };

  /**
   * Per-step completeness plus reachability. A step is unlocked only when every step
   * before it passes canAdvance — the source's positional lock let a deep link past a
   * gate it hadn't satisfied. An optional step never closes the path behind it.
   */
  const stepStates = useMemo(() => {
    if (!draft) return [] as { unlocked: boolean; complete: boolean }[];
    let reachable = true;
    return STEPS.map((step, i) => {
      const complete = canAdvance(draft, i + 1);
      const state = { unlocked: reachable, complete };
      if (!complete && !step.optional) reachable = false;
      return state;
    });
  }, [draft]);

  // The furthest step the current answers justify: the first incomplete gated step, or the
  // last step once everything gated passes.
  //
  // Except on a record that is no longer a draft, where every step is reachable. Forward
  // gating exists to stop someone skipping ahead during intake; once a record has been
  // submitted the wizard is its editor, not its intake flow, and `handleFinish` already
  // short-circuits for a non-draft record.
  //
  // Gating a non-draft record actively breaks it. Every use case approved before the AI Act
  // fields existed is active with an empty classification, so the classification step never
  // completes, so the oversight step behind it is unreachable — and that step holds the
  // human oversight, vendors, transfers, monitoring and review cadence that an approved
  // record is specifically allowed to keep current. The result was a dead end on precisely
  // the records that have been live longest.
  const maxReachable = draft && draft.status !== 'draft'
    ? STEPS.length
    : stepStates.length
      ? (stepStates.findIndex((s, i) => !s.complete && !STEPS[i].optional) + 1 || STEPS.length)
      : 1;

  // A record the API will not write is handed to the detail page rather than opened in an
  // editor that cannot save. Ref-guarded because StrictMode runs effects twice.
  useEffect(() => {
    if (!draft || !useCaseId || bouncedReadOnly.current) return;
    if (!READ_ONLY_STATUSES.has(draft.status)) return;
    bouncedReadOnly.current = true;
    toast.info(
      draft.status === 'rejected'
        ? `${draft.useCaseId} was rejected and is read-only. Reopen it as a draft to edit it.`
        : `${draft.useCaseId} is retired and is read-only.`,
    );
    navigate(`/ai-use-cases/${useCaseId}`, { replace: true });
  }, [draft, useCaseId, navigate, toast]);

  // Bounce a bad or not-yet-earned step in the URL back to somewhere legitimate.
  useEffect(() => {
    if (isNew || !useCaseId || !draft) return;
    if (!Number.isFinite(parsedStep) || parsedStep < 1 || parsedStep > STEPS.length) {
      navigate(`/ai-use-cases/${useCaseId}/intake/1`, { replace: true });
      return;
    }
    if (parsedStep > maxReachable) {
      navigate(`/ai-use-cases/${useCaseId}/intake/${maxReachable}`, { replace: true });
    }
  }, [isNew, useCaseId, draft, parsedStep, maxReachable, navigate]);

  const goToStep = async (target: number) => {
    if (!draft || !useCaseId) return;
    if (!(await persist(draft))) return;
    navigate(`/ai-use-cases/${useCaseId}/intake/${target}`);
  };

  const handleSaveAndExit = async () => {
    if (!draft) return;
    if (!(await persist(draft))) return;
    toast.success('Draft saved.');
    navigate('/ai-use-cases');
  };

  const handleBack = async () => {
    if (!draft || !useCaseId) return;
    if (parsedStep <= 1) {
      if (await persist(draft)) navigate('/ai-use-cases');
      return;
    }
    await goToStep(parsedStep - 1);
  };

  const handleFinish = async () => {
    if (!draft || !useCaseId) return;

    // The state machine only accepts draft -> submitted, so re-running the wizard over
    // an already-submitted record just saves and hands back to the detail page.
    if (draft.status !== 'draft') {
      if (await persist(draft)) navigate(`/ai-use-cases/${useCaseId}`);
      return;
    }

    // Step9Review is the confirmation now - a real read-only digest of everything above,
    // reached only once every gated step is complete - so a second, content-free swal.confirm
    // here would just be a blind "are you sure" over content the user just looked at.

    // The API validates the STORED record on transition, so the final step's edits have
    // to land before the status moves.
    if (!(await persist(draft))) return;
    submitMutation.mutate();
  };

  if (error) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>
          <span>This AI use case could not be loaded. It may have been deleted.</span>
        </div>
      </div>
    );
  }

  if (isNew || isLoading || !draft) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{isNew ? 'Creating draft…' : 'Loading…'}</span>
        </div>
        <p className="np-sub mt-3">{isNew ? 'Starting a new AI use case…' : 'Loading intake…'}</p>
      </div>
    );
  }

  // Clamp for rendering so the body never blanks during the redirect tick above.
  const activeStep = Math.min(Math.max(Number.isFinite(parsedStep) ? parsedStep : 1, 1), STEPS.length);
  const meta = STEPS[activeStep - 1];
  const canGo = canAdvance(draft, activeStep);
  const isFinalStep = activeStep === STEPS.length;
  const gatedComplete = STEPS.reduce(
    (count, step, i) => (!step.optional && stepStates[i]?.complete ? count + 1 : count),
    0,
  );
  const progress = Math.round((gatedComplete / GATED_STEP_COUNT) * 100);
  const busy = saveMutation.isPending || submitMutation.isPending;

  const classification = draft.aiActClassification || '';
  /** Article 5. Not a completeness problem — a stop condition, so it disables forward
   *  navigation and Submit outright rather than being listed among missing fields. */
  const prohibited = isBlocking(classification);
  const outstanding = STEPS
    .map((step, i) => ({ step, number: i + 1, complete: stepStates[i]?.complete ?? false }))
    .filter(s => !s.step.optional && !s.complete);
  const submitBlocked = draft.status === 'draft' && (prohibited || outstanding.length > 0);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-robot-line"
        title={draft.name || 'New AI Use Case'}
        subtitle={`Step ${activeStep} of ${STEPS.length} — ${meta.label}`}
        breadcrumbs={[
          { label: 'AI Use Cases', to: '/ai-use-cases' },
          { label: draft.name || draft.useCaseId, to: `/ai-use-cases/${draft.useCaseId}` },
          { label: 'Intake' },
        ]}
        actions={
          <>
            {savingStatus === 'saving' && (
              <span className="np-sub"><span className="spinner-border spinner-border-sm me-1"></span>Saving…</span>
            )}
            {savingStatus === 'saved' && (
              <span className="np-sub text-success"><i className="ri-check-line me-1"></i>Saved</span>
            )}
            {savingStatus === 'error' && (
              <span className="np-sub text-danger"><i className="ri-error-warning-line me-1"></i>Save failed</span>
            )}
            <button
              type="button"
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => { void handleSaveAndExit(); }}
              disabled={busy}
            >
              <i className="ri-save-line"></i>Save &amp; exit
            </button>
          </>
        }
      />

      <div className="np-detail-layout">
        {/* ── Step rail ── */}
        <div className="np-meta-rail">
          <div className="np-card">
            <div className="np-card-head">
              <i className="ri-list-ordered text-primary"></i>
              <h2>Intake Steps</h2>
              <span className="np-chip np-tone-neutral np-tabular">{progress}%</span>
            </div>

            <div className="np-nav-list">
              {STEPS.map((s, i) => {
                const stepNum = i + 1;
                const state = stepStates[i] ?? { unlocked: stepNum === 1, complete: false };
                const isActive = stepNum === activeStep;
                const optional = !!s.optional;
                // An optional step always passes canAdvance, so its rail state reflects whether
                // anything is actually there — read from the artefacts API, the only thing that
                // knows (see the linkedCount query).
                const done = optional ? linkedCount > 0 : state.complete;
                const locked = !state.unlocked && !isActive;

                return (
                  <button
                    key={s.label}
                    type="button"
                    className={`np-nav-item ${isActive ? 'active' : ''} ${done && !isActive ? 'done' : ''}`}
                    disabled={locked || busy}
                    aria-current={isActive ? 'step' : undefined}
                    onClick={() => { if (!isActive) void goToStep(stepNum); }}
                  >
                    <i className={`${locked ? 'ri-lock-line' : s.icon} np-nav-item-icon`}></i>
                    <div className="np-nav-item-body">
                      <div className="np-nav-item-title">{stepNum}. {s.label}</div>
                      <div className="np-nav-item-meta">
                        <span className="np-nav-item-count">
                          {locked
                            ? 'Complete earlier steps first'
                            : optional
                              ? done
                                ? `${linkedCount} linked`
                                : 'Optional'
                              : done
                                ? 'Complete'
                                : isActive
                                  ? 'In progress'
                                  : 'Incomplete'}
                        </span>
                      </div>
                    </div>
                    {done && <i className={`ri-check-line ${isActive ? '' : 'text-success'}`}></i>}
                  </button>
                );
              })}
            </div>

            <div className="np-card-body py-3">
              <div className="np-progress">
                <div className={`np-fill ${progress === 100 ? 'np-tone-success' : ''}`} style={{ width: `${progress}%` }} />
              </div>
              <p className="np-hint">{gatedComplete} of {GATED_STEP_COUNT} required steps complete</p>
            </div>
          </div>

          {/* ── Record meta ── */}
          <div className="np-card">
            <div className="np-card-body">
              <div className="np-meta-row">
                <span className="np-meta-label"><i className="ri-hashtag"></i>Reference</span>
                <span className="np-meta-value np-tabular">{draft.useCaseId}</span>
              </div>
              <div className="np-meta-row">
                <span className="np-meta-label"><i className="ri-flag-line"></i>Status</span>
                <span className="np-meta-value">
                  <span className={`np-chip np-tone-${AI_STATUS_TONE[draft.status]}`}>{AI_STATUS_LABELS[draft.status]}</span>
                </span>
              </div>
              <div className="np-meta-row">
                <span className="np-meta-label"><i className="ri-robot-line"></i>AI type</span>
                <span className="np-meta-value">{draft.aiType ? AI_TYPE_LABELS[draft.aiType] : 'Not set'}</span>
              </div>
              <div className="np-meta-row">
                <span className="np-meta-label"><i className="ri-scales-3-line"></i>AI Act tier</span>
                <span className="np-meta-value">
                  <span className={`np-chip np-tone-${AI_ACT_CLASSIFICATION_TONE[classification]}`}>
                    {AI_ACT_CLASSIFICATION_LABELS[classification]}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Step body ── */}
        <div>
          <div className="np-card">
            <div className="np-card-head">
              <span className="np-icon-badge np-tone-primary"><i className={meta.icon}></i></span>
              <h2>{meta.label}</h2>
              <span className="np-chip np-tone-neutral">Step {activeStep} of {STEPS.length}</span>
              {meta.optional && <span className="np-chip np-tone-neutral">Optional</span>}
            </div>
            <div className="np-card-body">
              {activeStep === 1 && <Step1AiType useCase={draft} update={update} />}
              {activeStep === 2 && <Step2Identity useCase={draft} update={update} />}
              {activeStep === 3 && <Step3BusinessCase useCase={draft} update={update} />}
              {activeStep === 4 && <Step4Outcomes useCase={draft} update={update} />}
              {activeStep === 5 && <Step5Data useCase={draft} update={update} />}
              {activeStep === 6 && <Step6Classification useCase={draft} update={update} />}
              {activeStep === 7 && <Step7Links useCase={draft} update={update} />}
              {activeStep === 8 && <Step8Oversight useCase={draft} update={update} />}
              {activeStep === 9 && <Step9Review useCase={draft} update={update} />}
            </div>
          </div>

          {/* The Article 5 stop, repeated outside step 7 so it travels with the user. It is the
           *  only message shown when it applies: burying it among "this field is required"
           *  invites the user to fix the twelve and try again. The server does the same. */}
          {prohibited ? (
            <div className="np-banner np-tone-danger mt-3" role="alert">
              <i className="ri-forbid-2-line"></i>
              <div>
                <strong>Prohibited AI practice — intake cannot continue.</strong> This use case is
                classified as prohibited under Article 5 of the EU AI Act, so it cannot be advanced,
                submitted or approved. Correct the classification on step 7 if it is wrong;
                otherwise the use case has to be abandoned.
              </div>
            </div>
          ) : (
            <>
              {!canGo && (
                <div className="np-banner np-tone-warning mt-3">
                  <i className="ri-error-warning-line"></i>
                  <span>
                    Please complete all required fields on this step before
                    {isFinalStep ? ' submitting' : ' continuing'}.
                  </span>
                </div>
              )}
              {canGo && isFinalStep && outstanding.length > 0 && (
                <div className="np-banner np-tone-warning mt-3">
                  <i className="ri-error-warning-line"></i>
                  <div>
                    Still needed before this can be submitted:{' '}
                    {outstanding.map(o => `step ${o.number} (${o.step.label})`).join(', ')}.
                  </div>
                </div>
              )}
            </>
          )}

          {(draft.status === 'approved' || draft.status === 'active') && (
            <div className="np-banner np-tone-info mt-3">
              <i className="ri-information-line"></i>
              <div>
                {draft.status === 'active'
                  ? 'This use case is active — its compliance checklist is fully satisfied.'
                  : 'This use case has been approved. It becomes Active once its compliance ' +
                    'checklist (DPIA, Subprocessor, Processing Activity, Privacy Policy) is fully ' +
                    'satisfied — see the Pending AI Use Cases panel on the register.'}{' '}
                Ownership, oversight, vendors, transfers, monitoring and the
                review cadence can still be changed; the rest was approved as recorded and is
                locked. If something else has to change, retire this use case and raise a new one.
              </div>
            </div>
          )}

          <hr className="np-divider" />

          <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="np-btn np-btn-ghost"
              onClick={() => { void handleBack(); }}
              disabled={busy}
            >
              <i className="ri-arrow-left-line"></i>{activeStep === 1 ? 'Cancel' : 'Back'}
            </button>

            {isFinalStep ? (
              <button
                type="button"
                className="np-btn np-btn-success"
                onClick={() => { void handleFinish(); }}
                disabled={busy || submitBlocked}
                title={submitBlocked ? 'Some required answers are still missing.' : undefined}
              >
                {submitMutation.isPending
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Submitting…</>
                  : <><i className="ri-check-double-line"></i>{draft.status === 'draft' ? 'Submit Use Case' : 'Save & Close'}</>}
              </button>
            ) : (
              <button
                type="button"
                className="np-btn np-btn-primary"
                onClick={() => { void goToStep(activeStep + 1); }}
                disabled={!canGo || busy}
              >
                Continue<i className="ri-arrow-right-line"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiUseCaseWizard;
